"use client";

import Image from "next/image";
import before from "@/public/before.webp";
import after from "@/public/after.webp";
import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";

type Props = {
  beforeImage?: string | undefined;
  afterImage?: string | undefined;
  beforeLabel?: string;
  afterLabel?: string;
  className?: string;
};

export function Compare({
  beforeImage,
  afterImage,
  beforeLabel = "Prije",
  afterLabel = "Poslije",
  className,
}: Props) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = (x / rect.width) * 100;
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    },
    [isDragging]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return;
      handleMove(e.touches[0].clientX);
    },
    [isDragging]
  );

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative w-[100vw] mx-auto h-[550px] overflow-hidden select-none cursor-col-resize",
        className
      )}
      data-header-theme="dark"
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
    >
      <div className="absolute inset-0">
        <Image
          src={afterImage || after}
          alt={afterLabel}
          className="size-full object-cover object-[50%_50%]"
          draggable={false}
          sizes="(max-width: 768px) 100vw"
          fill
        />
        <div className="absolute top-0 right-0 bg-brand-700 text-background py-4 px-8 text-sm font-medium">
          {afterLabel}
        </div>
      </div>

      <div
        className="absolute inset-0 transition-opacity duration-100"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <Image
          src={beforeImage || before}
          alt={beforeLabel}
          className="size-full object-cover object-[50%_38%]"
          sizes="(max-width: 768px) 100vw"
          draggable={false}
          fill
        />
        <div className="absolute top-0 left-0 bg-brand-700 text-background py-4 px-8 text-sm font-medium ">
          {beforeLabel}
        </div>
      </div>

      <div
        className="absolute top-0 bottom-0 w-1 bg-background transition-opacity duration-100"
        style={{ left: `${sliderPosition}%` }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-background flex items-center justify-center transition-transform duration-150 hover:scale-110 active:scale-95">
          <div className="flex gap-1">
            <div className="w-0.5 h-4 bg-gray-400" />
            <div className="w-0.5 h-4 bg-gray-400" />
          </div>
        </div>
      </div>
    </div>
  );
}
